/**
 * Whether a lazily loaded page that failed to load should reload the app.
 *
 * After a deploy, a tab opened before it asks for chunks whose hashed names
 * are gone, and a reload gets the new version. That is a script chunk
 * (.chunk.js), or the CSS chunk of a page with its own stylesheet
 * (.chunk.css), which webpack asks for first; both failures name the file in
 * `request`. A chunk that still fails after the reload (an ad blocker, a
 * content policy, a network that drops it) would reload the app forever, so
 * each chunk reloads it once a session; after that the page shows its load
 * error instead. A new deploy names new chunks, which reload again.
 */
export const CHUNK_FILE = /chunk\.(js|css)$/;

type SessionStore = Pick<Storage, "getItem" | "setItem">;

// (reading sessionStorage throws where the browser refuses it)
const sessionStore = (): SessionStore | undefined => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

// the chunks this page has started a reload for: the loading component is
// drawn again while the reload is on its way, and should not flash the error
const reloadsStarted = new Set<string>();

export const reloadForMissingChunk = (
  request: unknown,
  storage: SessionStore | undefined = sessionStore(),
  started: Set<string> = reloadsStarted
): boolean => {
  if (typeof request !== "string" || !CHUNK_FILE.test(request)) return false;
  if (started.has(request)) return true;
  const key = `reloaded-for-chunk:${request}`;
  try {
    if (storage && storage.getItem(key)) return false;
    if (storage) storage.setItem(key, "1");
  } catch {
    // session storage refused (a private window): reload, as before
  }
  started.add(request);
  return true;
};
